import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  useArchiveClient,
  useClient,
  useUpdateClient,
  type ClientCreateInput,
  type ClientFields,
} from "@/api/clients";
import { ContactsPanel } from "@/components/clients/ContactsPanel";
import { RateCardPanel } from "@/components/clients/RateCardPanel";
import { StatementPanel } from "@/components/clients/StatementPanel";
import { ClientForm } from "@/components/forms/ClientForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { TabPanel, Tabs, pickTab, type TabItem } from "@/components/ui/tabs";

const TABS = ["profile", "statement", "contacts", "rates"] as const;
type ClientTab = (typeof TABS)[number];
const TAB_ITEMS: TabItem<ClientTab>[] = [
  { id: "profile", label: "Profile" },
  { id: "statement", label: "Statement" },
  { id: "contacts", label: "Contacts" },
  { id: "rates", label: "Rate card" },
];

/** The code is permanent, so an edit sends every field except it. */
function editableFields(input: ClientCreateInput): ClientFields {
  const { code, ...fields } = input;
  void code;
  return fields;
}

export function ClientDetailPage() {
  const { clientId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = pickTab(params.get("tab"), TABS, "profile");
  const client = useClient(clientId);
  const update = useUpdateClient(clientId);
  const archive = useArchiveClient(clientId);

  if (client.isPending) {
    return <p className="text-muted-foreground">Loading client…</p>;
  }
  if (client.isError) {
    return <RequestError error={client.error} />;
  }

  const data = client.data;
  const archived = data.archived_at !== null;
  const reload = () => void client.refetch();

  return (
    <div className="flex flex-col gap-6">
      <Link to="/clients" className="text-sm text-muted-foreground hover:text-foreground">
        ← Clients
      </Link>
      <PageHeader
        title={data.legal_name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge>{data.code}</Badge>
            {archived && <Badge variant="muted">Archived</Badge>}
          </span>
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={archive.isPending}
            onClick={() => archive.mutate({ archived: !archived, version: data.version })}
          >
            {archived ? "Restore client" : "Archive client"}
          </Button>
        }
      />
      <RequestError error={archive.error} onReload={reload} />

      <div>
        <Tabs
          label="Client sections"
          items={TAB_ITEMS}
          value={tab}
          onChange={(next) => setParams(next === "profile" ? {} : { tab: next }, { replace: true })}
        />
        <TabPanel id={tab}>
          {tab === "profile" && (
            <Card>
              <CardContent>
                <ClientForm
                  key={data.version}
                  mode="edit"
                  initial={data}
                  submitLabel="Save changes"
                  onSubmit={(input) =>
                    update.mutateAsync({ input: editableFields(input), version: data.version })
                  }
                  onReload={reload}
                />
              </CardContent>
            </Card>
          )}
          {tab === "statement" && <StatementPanel clientId={data.id} />}
          {tab === "contacts" && <ContactsPanel clientId={data.id} archived={archived} />}
          {tab === "rates" && (
            <RateCardPanel clientId={data.id} currency={data.currency} archived={archived} />
          )}
        </TabPanel>
      </div>
    </div>
  );
}
